/**
 * Componente temporal para debuggear el rol del usuario
 * Accede a /debug-role para ver la información
 */

import React from 'react';
import { Box, Card, CardContent, Typography, Alert } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../hooks/useUserRole';

const DebugUserRole: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin, rol, loading, usuario } = useUserRole(user);

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Usuario no autenticado. Por favor inicia sesión.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        🔍 Debug: Información del Usuario Django
      </Typography>

      {/* Usuario de AuthContext */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            1. Usuario desde AuthContext
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2">
              <strong>ID:</strong> {user.id}
            </Typography>
            <Typography variant="body2">
              <strong>Username:</strong> {user.username}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {user.email}
            </Typography>
            <Typography variant="body2">
              <strong>Rol directo:</strong> <code>{user.rol || 'No definido'}</code>
            </Typography>
            <Typography variant="body2">
              <strong>es_admin:</strong> {user.es_admin ? '✅ true' : '❌ false'}
            </Typography>
            <Typography variant="body2">
              <strong>es_cliente:</strong> {user.es_cliente ? '✅ true' : '❌ false'}
            </Typography>
            <Typography variant="body2">
              <strong>es_repartidor:</strong> {user.es_repartidor ? '✅ true' : '❌ false'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Perfil del Usuario */}
      {user.profile && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              2. Perfil del Usuario
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2">
                <strong>Rol en perfil:</strong> <code>{user.profile.rol || 'No definido'}</code>
              </Typography>
              <Typography variant="body2">
                <strong>es_admin (perfil):</strong> {user.profile.es_admin ? '✅ true' : '❌ false'}
              </Typography>
              <Typography variant="body2">
                <strong>es_cliente (perfil):</strong> {user.profile.es_cliente ? '✅ true' : '❌ false'}
              </Typography>
              <Typography variant="body2">
                <strong>es_repartidor (perfil):</strong> {user.profile.es_repartidor ? '✅ true' : '❌ false'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Hook useUserRole */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            3. Hook useUserRole (Estado Actual)
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2">
              <strong>loading:</strong> {loading ? '✅ true' : '❌ false'}
            </Typography>
            <Typography variant="body2">
              <strong>rol:</strong> <code>{rol}</code>
            </Typography>
            <Typography variant="body2">
              <strong>isAdmin:</strong> {isAdmin ? '✅ true' : '❌ false'}
            </Typography>
            <Typography variant="body2">
              <strong>usuario (objeto):</strong> {usuario ? 'Cargado ✅' : 'null ❌'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Diagnóstico */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            4. Diagnóstico
          </Typography>
          {isAdmin ? (
            <Alert severity="success">
              ✅ El usuario tiene permisos de ADMINISTRADOR
              <br />
              El menú Dashboard debería estar visible.
            </Alert>
          ) : (
            <Alert severity="error">
              ❌ El usuario NO tiene permisos de administrador
              <br />
              Rol actual: <strong>{rol}</strong>
              <br />
              Para ver el Dashboard, necesitas que un administrador te asigne el rol 'admin'.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DebugUserRole;